import { Injectable, inject } from '@angular/core';

import { TODO } from '@b2b/types/core/utils';

import { TemplatesService } from '../templates/templates.service';
import { AuditTrailConfigsService } from './audit-trail-configs.service';
import { AuditTrailVersionHandlerService } from './audit-trail-version-handler.service';

/**
 * AuditTrailGridsService — Angular 18.
 *
 * Ported 1:1 from
 * apps/b2b-ui/common/services/auditTrail/auditTrailGrids/auditTrailGrids.service.js
 * (legacy AngularJS name 'AuditTrailGridsService').
 *
 * Builds the diff grid models (updated/original/new/removed rows) for each
 * grid-able type on an audit-trail version, delegating row formatting to
 * AuditTrailConfigsService handlers and resource-request detection to
 * TemplatesService.
 *
 * Registered back into AngularJS under 'AuditTrailGridsService' via
 * downgradeInjectable in app/upgrade/ng-downgraded-components.ts.
 */
@Injectable({ providedIn: 'root' })
export class AuditTrailGridsService {
  private config = inject(AuditTrailConfigsService);
  private versionHandler = inject(AuditTrailVersionHandlerService);
  private templates = inject(TemplatesService);

  type = '';

  init(): boolean {
    return true;
  }

  getGridValuesByType(updated: TODO, original: TODO, type: TODO): TODO {
    let res: TODO = {};
    if (type === 'priceLists') {
      res.updated = updated !== undefined ? updated[0].prices : updated;
      res.original = original !== undefined ? original[0].prices : original;
    }
    if (type === 'priceList') {
      res.updated = updated !== undefined ? updated.prices : updated;
      res.original = original !== undefined ? original.prices : original;
    }
    if (
      type === 'resources' ||
      type === 'flexPays' ||
      type === 'prices' ||
      type === 'values' ||
      type === 'resourceRequests' ||
      type === 'standard' ||
      type === 'exceptions' ||
      type === 'addOnsCombo'
    ) {
      res.updated = updated;
      res.original = original;
    }
    if (type === 'schedules') {
      const version = this.versionHandler.getVersion();
      if (version.auditTrail.businessEvent.indexOf('event.template.schedule.resources') !== -1) {
        res = this.processResourceRequest(updated, original);
      } else {
        res.updated = updated;
        res.original = original;
      }
    }

    if (type === 'addOns') {
      res.updated = updated !== undefined ? updated.inventoryAddOns : updated;
      res.original = original !== undefined ? original.inventoryAddOns : original;
    }

    if (type === 'membership') {
      res.updated = updated !== undefined ? updated.discounts : updated;
      res.original = original !== undefined ? original.discounts : original;
    }

    return res;
  }

  /**
   * Determines grid type and builds model depending on its _updated and
   * _original then adds the model to the corresponding version property.
   */
  processGridType(_updated: TODO, _original: TODO, type: TODO): void {
    this.type = type;

    let model: TODO;
    const dataset = this.getGridValuesByType(_updated, _original, type);
    const updated = dataset.updated;
    const original = dataset.original;

    const handlers = this.config.getCallbackHandlers(this.type);
    const accessor = this.config.getAccessor(this.type);

    if (original && original.length && updated && updated.length) {
      model = this.processGridModel(updated, original, accessor, handlers);
      model.flags = this.generateFlags(true, false, false);
    } else if (updated === undefined || (!updated.length && original)) {
      model = this.processRemovedGridModel(original || {}, accessor || {}, handlers);
      model.flags = this.generateFlags(false, false, true);
    } else {
      model = this.processNewGridModel(updated, accessor, handlers);
      model.flags = this.generateFlags(true, true, false);
    }
    if (model) {
      this.versionHandler.addToVersion('grids', this.generateGridModel(model, this.type));
    }
  }

  generateGridModel(model: TODO, type: TODO): TODO {
    return {
      headers: this.config.getGridHeaders(type),
      updated: model.updated,
      original: model.original,
      newValues: model.newValues,
      removed: model.removed,
      breadcrumb: this.config.getGridBreadCrumbs(type),
      flags: model.flags,
    };
  }

  generateFlags(isPrevious: boolean, isNewValue: boolean, isRemoved: boolean): TODO {
    return {
      isPrevious: isPrevious,
      isNewValue: isNewValue,
      isRemoved: isRemoved,
    };
  }

  /**
   * Build model for a grid that contains an updated and original value,
   * compares both and builds the dataset in relation to the matching branch.
   */
  processGridModel(_updated: TODO, _original: TODO, accessor: TODO, handlers: TODO): TODO {
    const updated = _updated[accessor] || _updated;
    const original = _original[accessor] || _original;
    const dataset: TODO = {
      updated: [],
      original: [],
      newValues: [],
      removed: [],
    };

    if (original.length > updated.length) {
      original.forEach(function (val: TODO, idx: number) {
        dataset.original.push(handlers.row('original', val));
        if (updated[idx]) {
          dataset.updated.push(handlers.rowByCondition('original', 'replaced', val, updated[idx]));
        } else {
          dataset.updated.push(handlers.row('removed', val));
        }
      });
    }

    if (original.length < updated.length) {
      original.forEach(function (val: TODO, idx: number) {
        const maxLength = original.length - 1;
        dataset.original.push(handlers.row('original', val));
        if (maxLength === idx) {
          for (let i = maxLength, ii = updated.length; i < ii; i++) {
            if (idx === i) {
              dataset.updated.push(handlers.rowByCondition('original', 'replaced', val, updated[idx]));
            } else {
              dataset.updated.push(handlers.row('replaced', updated[i]));
            }
          }
        } else {
          dataset.updated.push(handlers.rowByCondition('original', 'replaced', val, updated[idx]));
        }
      });
    }

    if (original.length === updated.length) {
      original.forEach(function (val: TODO, idx: number) {
        dataset.original.push(handlers.row('original', val));
        dataset.updated.push(handlers.rowByCondition('original', 'replaced', val, updated[idx]));
      });
    }

    return dataset;
  }

  /**
   * Builds model for a NEW grid, adds values to the newValues property.
   */
  processNewGridModel(_newValues: TODO, accessor: TODO, handlers: TODO): TODO {
    const obj = _newValues[accessor] || _newValues;
    let dataset: TODO;
    if (obj.length) {
      dataset = { updated: [], original: [], newValues: [], removed: [] };
      obj.forEach(function (val: TODO) {
        dataset.newValues.push(handlers.row('original', val));
      });
    } else {
      dataset = false;
    }
    return dataset;
  }

  /**
   * Builds model, adds value to the removed property.
   */
  processRemovedGridModel(_removed: TODO, accessor: TODO, handlers: TODO): TODO {
    const obj = _removed[accessor] || _removed;
    let dataset: TODO;
    if (obj.length) {
      dataset = { updated: [], original: [], newValues: [], removed: [] };
      obj.forEach(function (val: TODO) {
        dataset.removed.push(handlers.row('removed', val));
      });
    } else {
      dataset = false;
    }
    return dataset;
  }

  /**
   * Builds model, checks for resourceRequest, adds it to the dataset obj and
   * sets the correct type.
   */
  processResourceRequest(updated: TODO, original: TODO): TODO {
    const dataset: TODO = {};

    updated.forEach((val: TODO) => {
      if (this.templates.hasScheduleResourceRequests(val)) {
        dataset.updated = val.resourceRequests;
      }
    });
    original.forEach((val: TODO) => {
      if (this.templates.hasScheduleResourceRequests(val)) {
        dataset.original = val.resourceRequests;
      }
    });

    dataset.original = original.some((val: TODO) => this.templates.hasScheduleResourceRequests(val))
      ? dataset.original
      : [];
    dataset.updated = updated.some((val: TODO) => this.templates.hasScheduleResourceRequests(val))
      ? dataset.updated
      : [];
    this.type = 'resourceRequests';

    return dataset;
  }
}
